import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { I18nProvider } from "@/i18n/I18nProvider";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import AddProduct from "@/pages/AddProduct";
import Favorites from "@/pages/Favorites";
import TemperatureLogs from "@/pages/TemperatureLogs";
import Refrigerators from "@/pages/Refrigerators";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Layout from "@/components/Layout";

import CoolingCell from "@/pages/CoolingCell";
import ClosingChecklist from "@/pages/ClosingChecklist";
import Suppliers from "@/pages/Suppliers";
import PurchaseList from "@/pages/PurchaseList";
import Labels from "@/pages/Labels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API instance with credentials
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Flag to prevent multiple session-expired toasts
let sessionExpiredShown = false;

// Auth Provider
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const forceLogout = useCallback(() => {
    setUser(null);
    // Show session expired toast only once
    if (!sessionExpiredShown) {
      sessionExpiredShown = true;
      toast.error("Session expired. Please log in again.");
      setTimeout(() => { sessionExpiredShown = false; }, 3000);
    }
  }, []);

  useEffect(() => {
    // Setup 401 interceptor
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && user) {
          // Token expired or invalid while user was logged in
          forceLogout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [user, forceLogout]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, pin) => {
    const response = await api.post("/auth/login", { email, pin });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // We still want the client to consider itself logged out even if the
      // network call fails — but log the error so production issues surface.
      console.warn("[Auth] Logout request failed (clearing client session anyway):", e?.message || e);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRouter() {
  return (
    <I18nProvider>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Layout>
              <Products />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/add"
        element={
          <ProtectedRoute>
            <Layout>
              <AddProduct />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <AddProduct />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <Layout>
              <Favorites />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/temperature"
        element={
          <ProtectedRoute>
            <Layout>
              <TemperatureLogs />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/refrigerators"
        element={
          <ProtectedRoute>
            <Layout>
              <Refrigerators />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/closing-checklist"
        element={
          <ProtectedRoute>
            <Layout>
              <ClosingChecklist />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-list"
        element={
          <ProtectedRoute>
            <Layout>
              <PurchaseList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            <Layout>
              <Suppliers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cooling-cell"
        element={
          <ProtectedRoute>
            <Layout>
              <CoolingCell />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/labels"
        element={
          <ProtectedRoute>
            <Layout>
              <Labels />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <Reports />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </I18nProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
